import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { toggleSx } from "./BaseMapToggle";
import { useTranslation } from "react-i18next";
import { dateLocale, useLocalize } from "../i18n";

const STATUSES = {
  unreviewed: {
    actions: [
      {
        action: "reviewed",
        Icon: CheckCircleOutlineIcon,
        color: "success.main",
      },
      {
        action: "invalidate",
        Icon: BlockOutlinedIcon,
        color: "error.main",
      },
    ],
  },
  reviewed: {
    actions: [
      {
        action: "unreviewed",
        Icon: UndoOutlinedIcon,
        flip: true, // undo turns round in Arabic
        color: "text.secondary",
      },
    ],
  },
  invalid: {
    actions: [
      {
        action: "validate",
        Icon: RestoreOutlinedIcon,
        color: "success.main",
      },
    ],
  },
};

const formatDate = (ymd, language) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(dateLocale(language), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export default function FieldAlerts({
  alerts,
  loading,
  busy,
  onAction,
  onShowOnMap,
  onShowAll,
}) {
  const [status, setStatus] = useState("unreviewed");
  const { t, i18n } = useTranslation();
  const localize = useLocalize();
  const [selected, setSelected] = useState(new Set());

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!alerts) {
    return (
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {t("fieldAlerts.loadFailed")}
      </Typography>
    );
  }

  const list = alerts[status] || [];
  const count = alerts.counts?.[status] ?? { count: 0, max: 0 };
  const { actions } = STATUSES[status];

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const run = async (ids, action) => {
    if (await onAction(ids, action)) setSelected(new Set());
  };

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={status}
        onChange={(_, next) => {
          if (!next) return;
          setStatus(next);
          setSelected(new Set());
        }}
        aria-label={t("fieldAlerts.statusLabel")}
        sx={[toggleSx, { mb: 1 }]}
      >
        {Object.keys(STATUSES).map((key) => (
          <ToggleButton key={key} value={key}>
            {t(`fieldAlerts.status.${key}`)}
            <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
              {alerts.counts?.[key]?.count ?? 0}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {selected.size > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.5,
            mb: 1,
            borderRadius: "8px",
            bgcolor: "rgba(var(--primary-rgb), 0.08)",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              flex: 1,
              whiteSpace: "nowrap",
            }}
          >
            {t("fieldAlerts.selected", { count: selected.size })}
          </Typography>
          {actions.map(({ action }) => (
            <Button
              key={action}
              size="small"
              disabled={busy}
              onClick={() => run([...selected], action)}
              sx={{ textTransform: "none", fontSize: 12, minWidth: 0 }}
            >
              {t(`fieldAlerts.short.${action}`)}
            </Button>
          ))}
          <Button
            size="small"
            color="inherit"
            onClick={() => setSelected(new Set())}
            sx={{ textTransform: "none", fontSize: 12, minWidth: 0 }}
          >
            {t("common.clear")}
          </Button>
        </Box>
      )}

      {list.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1.5,
            color: "text.secondary",
          }}
        >
          <TaskAltOutlinedIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 12 }}>
            {t(`fieldAlerts.none.${status}`)}
          </Typography>
        </Box>
      ) : (
        list.map((alert) => (
          <Box
            key={alert.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 0.5,
              borderBottom: "1px solid #eef1f4",
            }}
          >
            <Checkbox
              size="small"
              checked={selected.has(alert.id)}
              onChange={() => toggle(alert.id)}
              disabled={busy}
              inputProps={{
                "aria-label": t("fieldAlerts.selectAlert", {
                  date: alert.date,
                }),
              }}
              sx={{ p: 0.5, "& .MuiSvgIcon-root": { fontSize: 18 } }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {formatDate(alert.date, i18n.resolvedLanguage)}
              </Typography>
              <Typography noWrap sx={{ fontSize: 11, color: "text.secondary" }}>
                {localize(alert, "index_name")} · {localize(alert, "metric")}{" "}
                {alert.percentage}%
              </Typography>
            </Box>
            <Tooltip title={t("fieldAlerts.showOnMap")}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t("fieldAlerts.showOnMap")}
                  disabled={busy}
                  onClick={() => onShowOnMap(alert)}
                  sx={{ color: "primary.main" }}
                >
                  <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            {actions.map(({ action, Icon, color, flip }) => (
              <Tooltip key={action} title={t(`fieldAlerts.actions.${action}`)}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={t(`fieldAlerts.actions.${action}`)}
                    disabled={busy}
                    onClick={() => run([alert.id], action)}
                    sx={{ color }}
                  >
                    <Icon
                      className={flip ? "flip-rtl" : undefined}
                      sx={{ fontSize: 18 }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </Box>
        ))
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1,
        }}
      >
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {count.count > count.max
            ? t("fieldAlerts.latest", { max: count.max, count: count.count })
            : ""}
        </Typography>
        <Button
          size="small"
          onClick={onShowAll}
          sx={{ textTransform: "none", fontSize: 12, minWidth: 0 }}
        >
          {t("fieldAlerts.openAll")}
        </Button>
      </Box>
    </Box>
  );
}
